import { useMemo } from 'react';
import { Source, Layer } from 'react-map-gl';
import type { LineLayer } from 'react-map-gl';
import { MapLocation } from '@/types/location';

interface MapboxNetworkLinesProps {
  filteredLocations: MapLocation[];
  selectedLocations: Set<number>;
}

export const MapboxNetworkLines = ({ filteredLocations, selectedLocations }: MapboxNetworkLinesProps) => {
  const geojsonData = useMemo(() => {
    if (selectedLocations.size < 2) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const selectedCoords = filteredLocations
      .filter((l) => selectedLocations.has(l.id))
      .map((l) => [l.coordinates[1], l.coordinates[0]] as [number, number]);

    if (selectedCoords.length < 2) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    return {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: selectedCoords,
          },
          properties: {},
        },
      ],
    };
  }, [filteredLocations, selectedLocations]);

  const lineLayer: LineLayer = {
    id: 'network-lines',
    type: 'line',
    source: 'network-lines',
    paint: {
      'line-color': '#3b82f6',
      'line-width': 2,
      'line-opacity': 0.6,
      'line-dasharray': [2, 2],
    },
  };

  const glowLayer: LineLayer = {
    id: 'network-lines-glow',
    type: 'line',
    source: 'network-lines',
    paint: {
      'line-color': '#3b82f6',
      'line-width': 6,
      'line-opacity': 0.15,
      'line-blur': 4,
    },
  };

  return (
    <Source id="network-lines" type="geojson" data={geojsonData}>
      <Layer {...glowLayer} />
      <Layer {...lineLayer} />
    </Source>
  );
};
